import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Fade,
  Collapse,
  Alert,
  Tooltip,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Popover,
} from "@mui/material";
import {
  Close,
  AutoStories,
  Download,
  ContentCopy,
  CheckCircle,
  PlayArrow,
  Refresh,
  MenuBook,
  FormatListBulleted,
  Article,
  LibraryBooks,
  Stars,
  KeyboardArrowDown,
  KeyboardArrowUp,
  EmojiObjects,
  FiberManualRecord,
  Image as ImageIcon,
  Copyright,
  FormatQuote,
  Summarize,
  History,
  PictureAsPdf,
  Description,
  Bookmark,
  BookmarkBorder,
  Share,
  ExpandMore,
  ChevronRight,
  ChevronLeft,
  Palette,
  Subject,
  FormatAlignLeft,
  Info,
  AutoAwesome,
  NavigateNext,
  NavigateBefore,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { generateEbook } from "../services/new_blogsServices";
import { getCheckSummariser } from "../services/summariserService";
import { withStyles } from "@material-ui/core/styles";

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);
// ─── COVER DISPLAY ───────────────────────────────────────────────────────────
const CoverCard = React.memo(({ cover, isSelected, onSelect }) => {
  const theme = useTheme();
  return (
    <Card
      onClick={() => onSelect(cover)}
      sx={{
        cursor: "pointer",
        border: isSelected
          ? `2px solid ${cover.palette[3] || "#4A90E2"}`
          : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 2.5,
        overflow: "hidden",
        transition: "all 0.25s ease",
        transform: isSelected ? "scale(1.02)" : "scale(1)",
        boxShadow: isSelected
          ? `0 8px 32px ${alpha(cover.palette[3] || "#4A90E2", 0.35)}`
          : "none",
        "&:hover": { transform: "scale(1.02)", boxShadow: theme.shadows[6] },
      }}
    >
      <Box
        sx={{
          height: 160,
          background: `linear-gradient(135deg, ${cover.palette[0]}, ${cover.palette[1]}, ${cover.palette[2]})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 30% 70%, ${alpha(cover.palette[3] || "#fff", 0.25)}, transparent 60%)`,
          }}
        />
        <Palette sx={{ fontSize: 40, color: alpha("#fff", 0.6) }} />
        {isSelected && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: cover.palette[3] || "#10b981",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle sx={{ fontSize: 16, color: "white" }} />
          </Box>
        )}
      </Box>
      <CardContent sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ fontSize: "0.82rem" }}
        >
          {cover.concept}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.4, display: "block", mt: 0.3 }}
        >
          {cover.description}
        </Typography>
        <Stack direction="row" spacing={0.4} sx={{ mt: 1 }}>
          {cover.palette.map((c, i) => (
            <Box
              key={i}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: c,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
});

// ─── CHAPTER CARD ────────────────────────────────────────────────────────────
const ChapterFullView = React.memo(({ chapter, chapterColor }) => {
  const theme = useTheme();
  const [bookmarked, setBookmarked] = useState(false);

  const safeContent =
    typeof chapter.content === "string" ? chapter.content.slice(0, 5000) : "";

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(chapterColor, 0.2)}`,
        overflow: "hidden",
        mb: 3,
        background: alpha(chapterColor, 0.02),
      }}
    >
      {/* Chapter Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${alpha(chapterColor, 0.12)}, ${alpha(chapterColor, 0.05)})`,
          borderBottom: `1px solid ${alpha(chapterColor, 0.15)}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${chapterColor}, ${alpha(chapterColor, 0.65)})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: 800,
            fontSize: "1.1rem",
            flexShrink: 0,
            boxShadow: `0 4px 14px ${alpha(chapterColor, 0.35)}`,
          }}
        >
          {chapter.chapter}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.6rem",
              color: chapterColor,
              fontWeight: 700,
              letterSpacing: 1.5,
            }}
          >
            CHAPTER {chapter.chapter}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ lineHeight: 1.2, fontFamily: "'Georgia', serif" }}
          >
            {chapter.title}
          </Typography>
        </Box>
        <LightTooltip title={bookmarked ? "Remove bookmark" : "Bookmark"}>
          <IconButton
            size="small"
            onClick={() => setBookmarked(!bookmarked)}
            sx={{ color: bookmarked ? "#f59e0b" : "text.secondary" }}
          >
            {bookmarked ? <Bookmark /> : <BookmarkBorder />}
          </IconButton>
        </LightTooltip>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Intro */}
        <Box
          sx={{
            mb: 3,
            pl: 2,
            borderLeft: `3px solid ${alpha(chapterColor, 0.5)}`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.6rem",
              color: chapterColor,
              fontWeight: 700,
              letterSpacing: 1.2,
              display: "block",
              mb: 0.8,
            }}
          >
            INTRODUCTION
          </Typography>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.9,
              color: "text.primary",
              fontFamily: "'Georgia', serif",
              fontStyle: "italic",
            }}
          >
            {chapter.intro}
          </Typography>
        </Box>

        {/* Quote */}
        <Box
          sx={{
            my: 3,
            p: 2.5,
            borderRadius: 2,
            background: alpha(chapterColor, 0.06),
            border: `1px solid ${alpha(chapterColor, 0.15)}`,
            position: "relative",
          }}
        >
          <FormatQuote
            sx={{
              fontSize: 32,
              color: alpha(chapterColor, 0.4),
              position: "absolute",
              top: 8,
              left: 12,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Georgia', serif",
              fontStyle: "italic",
              fontSize: "1.05rem",
              color: "text.primary",
              pl: 4,
              lineHeight: 1.7,
            }}
          >
            {chapter.quote}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: chapterColor,
              fontWeight: 700,
              pl: 4,
              display: "block",
              mt: 1,
            }}
          >
            — {chapter.quoteAuthor || "Anonymous"}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.6rem",
              color: chapterColor,
              fontWeight: 700,
              letterSpacing: 1.2,
              display: "block",
              mb: 1.5,
            }}
          >
            CONTENT
          </Typography>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 2,
              color: "text.primary",
              fontFamily: "'Georgia', serif",
              whiteSpace: "pre-line",
            }}
          >
            {safeContent}
          </Typography>
        </Box>

        {/* Conclusion */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: alpha(chapterColor, 0.06),
            borderTop: `3px solid ${chapterColor}`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.6rem",
              color: chapterColor,
              fontWeight: 700,
              letterSpacing: 1.2,
              display: "block",
              mb: 0.8,
            }}
          >
            CONCLUSION
          </Typography>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.9,
              color: "text.primary",
              fontFamily: "'Georgia', serif",
            }}
          >
            {chapter.conclusion}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const SECTION_TABS = [
  { id: "cover", label: "Cover", icon: <ImageIcon />, color: "#f59e0b" },
  {
    id: "copyright",
    label: "Copyright",
    icon: <Copyright />,
    color: "#6366f1",
  },
  { id: "title", label: "Title & Tagline", icon: <Stars />, color: "#ec4899" },
  {
    id: "outline",
    label: "Outline",
    icon: <FormatListBulleted />,
    color: "#3b82f6",
  },
  { id: "toc", label: "Contents", icon: <LibraryBooks />, color: "#8b5cf6" },
  { id: "chapters", label: "Chapters", icon: <MenuBook />, color: "#10b981" },
  { id: "history", label: "History", icon: <History />, color: "#14b8a6" },
];

const CHAPTER_COLORS = [
  "#10b981",
  "#3b82f6",
  "#ec4899",
  "#f59e0b",
  "#8b5cf6",
  "#f97316",
  "#14b8a6",
  "#ef4444",
  "#06b6d4",
  "#a855f7",
];

const BOOK_HISTORY_STORAGE_KEY = "aiEbookHistoryByBook";

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const EBookScenario = ({
  open,
  onClose,
  pipelineContents,
  generators = [],
  bookType = "Fiction",
  language = "English",
  bookTitle: initialTitle = "The Art of Tomorrow",
  chapterCount: initialChapterCount = 5,
  authorName: initialAuthorName = "",
  authorName2: initialAuthorName2 = "",
  tone: initialTone = "professional",
}) => {
  const theme = useTheme();

  // ── STATE ─────────────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [ebookData, setEbookData] = useState(null);
  const [bookHistory, setBookHistory] = useState([]);
  const [activeBookHistoryKey, setActiveBookHistoryKey] = useState("");
  const [activeBookMeta, setActiveBookMeta] = useState(null);
  const [allBookHistories, setAllBookHistories] = useState({});
  const [activeTab, setActiveTab] = useState("cover");
  const [activeChapter, setActiveChapter] = useState(1);
  const [selectedCover, setSelectedCover] = useState(null);
  const [error, setError] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState("");
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [selectionSummary, setSelectionSummary] = useState("");
  const [selectionSummaryError, setSelectionSummaryError] = useState("");
  const [isSummarizingSelection, setIsSummarizingSelection] = useState(false);
  const [selectedTone, setSelectedTone] = useState(
    typeof initialTone === "string"
      ? initialTone.trim() || "professional"
      : String(initialTone || "").trim() || "professional",
  );
  const [authorName2, setAuthorName2] = useState(
    typeof initialAuthorName2 === "string"
      ? initialAuthorName2.trim()
      : String(initialAuthorName2 || "").trim(),
  );

  const selectionTimeoutRef = useRef(null);
  const bookHistoryRef = useRef(bookHistory);
 
  bookHistoryRef.current = bookHistory;

  const normalizeText = useCallback((value, fallback = "") => {
    if (typeof value === "string") return value;
    if (value == null) return fallback;
    return String(value);
  }, []);

  const normalizeArray = useCallback((value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) return [value];
    return [];
  }, []);

  const generateHistoryId = useCallback(
    () =>
      crypto.randomUUID
        ? crypto.randomUUID()
        : Date.now().toString() + Math.random().toString(36).slice(2, 10),
    [],
  );

  const resolvedAuthorName = useMemo(
    () => normalizeText(initialAuthorName, "").trim() || "Unknown Author",
    [initialAuthorName, normalizeText],
  );

  const buildBookHistoryKey = useCallback(
    (
      title = initialTitle,
      author = resolvedAuthorName,
      genre = bookType,
      currentLanguage = language,
    ) => {
      const safe = [title, author, genre, currentLanguage]
        .map((v) =>
          normalizeText(v, "untitled")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, ""),
        )
        .filter(Boolean)
        .join("__");
      return safe || "untitled-book";
    },
    [initialTitle, resolvedAuthorName, bookType, language, normalizeText],
  );

  const buildBookMeta = useCallback(
    (
      title = initialTitle,
      author = resolvedAuthorName,
      genre = bookType,
      currentLanguage = language,
    ) => ({
      title: normalizeText(title, initialTitle),
      author: normalizeText(author, resolvedAuthorName),
      genre: normalizeText(genre, bookType),
      language: normalizeText(currentLanguage, language),
    }),
    [initialTitle, resolvedAuthorName, bookType, language, normalizeText],
  );

  const stripPromptPrefix = useCallback(
    (value) => {
      const text = normalizeText(value).trim();
      if (!text) return "";
      const marker = "based on the details below:";
      const markerIndex = text.toLowerCase().indexOf(marker);
      if (markerIndex >= 0)
        return text.slice(markerIndex + marker.length).trim();
      return text;
    },
    [normalizeText],
  );

  const extractLabeledValue = useCallback(
    (value, label) => {
      const text = normalizeText(value);
      if (!text) return "";
      const regex = new RegExp(`${label}\\s*:\\s*"?([^"\\n]+)"?`, "i");
      const match = text.match(regex);
      return match?.[1]?.trim() || "";
    },
    [normalizeText],
  );

  const sanitizeDisplayText = useCallback(
    (value, fallback = "") => stripPromptPrefix(normalizeText(value, fallback)),
    [stripPromptPrefix, normalizeText],
  );

  const cleanDisplayTitle = useCallback(
    (titleValue, taglineValue) => {
      const strippedTitle = stripPromptPrefix(titleValue);
      const labeledTitle =
        extractLabeledValue(strippedTitle, "Title") ||
        extractLabeledValue(taglineValue, "Title");
      return labeledTitle || strippedTitle || initialTitle;
    },
    [stripPromptPrefix, extractLabeledValue, initialTitle],
  );

  const cleanDisplayTagline = useCallback(
    (taglineValue) => {
      const labeledTagline = extractLabeledValue(taglineValue, "Tagline");
      return labeledTagline || stripPromptPrefix(taglineValue);
    },
    [extractLabeledValue, stripPromptPrefix],
  );

  const extractSummaryText = useCallback((response) => {
    const rawOutput =
      response?.data?.data?.output ??
      response?.data?.output ??
      response?.output ??
      response?.data?.data ??
      response?.data ??
      response;

    if (Array.isArray(rawOutput)) {
      return rawOutput
        .map((item) =>
          typeof item === "string" ? item.trim() : String(item || "").trim(),
        )
        .filter(Boolean)
        .join(" ")
        .trim();
    }
    if (typeof rawOutput === "string") return rawOutput.trim();
    if (rawOutput && typeof rawOutput === "object") {
      if (typeof rawOutput.output === "string") return rawOutput.output.trim();
      if (Array.isArray(rawOutput.output))
        return rawOutput.output
          .map((item) =>
            typeof item === "string" ? item.trim() : String(item || "").trim(),
          )
          .filter(Boolean)
          .join(" ")
          .trim();
      if (typeof rawOutput.summary === "string")
        return rawOutput.summary.trim();
      if (typeof rawOutput.result === "string") return rawOutput.result.trim();
    }
    return "";
  }, []);

  // ── EFFECTS ───────────────────────────────────────────────────────────────

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setIsGenerating(false);
    setGenerationComplete(false);
    setEbookData(null);
    setActiveTab("cover");
    setActiveChapter(1);
    setSelectedCover(null);
    setError(null);
    setSelectedHighlight("");
    setSelectionAnchor(null);
    setSelectionSummary("");
    setSelectionSummaryError("");
    setIsSummarizingSelection(false);
    setSelectedTone(
      normalizeText(initialTone, "professional").trim() || "professional",
    );
    setAuthorName2(normalizeText(initialAuthorName2, "").trim());
    setActiveBookHistoryKey(
      buildBookHistoryKey(initialTitle, resolvedAuthorName, bookType, language),
    );
    setActiveBookMeta(
      buildBookMeta(initialTitle, resolvedAuthorName, bookType, language),
    );
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load from localStorage once on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(BOOK_HISTORY_STORAGE_KEY);
    if (!savedHistory) {
      setAllBookHistories({});
      return;
    }
    try {
      const parsedHistory = JSON.parse(savedHistory) || {};
      const normalizedHistory = Object.entries(parsedHistory).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc[key] = {
              meta: { title: key, author: "", genre: "", language: "" },
              history: value,
            };
          } else if (value && typeof value === "object") {
            acc[key] = {
              meta: value.meta || {
                title: key,
                author: "",
                genre: "",
                language: "",
              },
              history: Array.isArray(value.history) ? value.history : [],
            };
          }
          return acc;
        },
        {},
      );
      setAllBookHistories(normalizedHistory);
    } catch {
      setAllBookHistories({});
    }
  }, []);

  // FIX 1: Single debounced localStorage write — removed the duplicate sync effect
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        if (Object.keys(allBookHistories).length > 0) {
          localStorage.setItem(
            BOOK_HISTORY_STORAGE_KEY,
            JSON.stringify(allBookHistories),
          );
        } else {
          localStorage.removeItem(BOOK_HISTORY_STORAGE_KEY);
        }
      } catch (err) {
        console.warn("Storage write failed", err);
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [allBookHistories]);

  // Sync allBookHistories → bookHistory when key changes
  useEffect(() => {
    if (!activeBookHistoryKey) return;
    const currentBookEntry = allBookHistories[activeBookHistoryKey];
    setBookHistory(
      Array.isArray(currentBookEntry?.history) ? currentBookEntry.history : [],
    );
  }, [activeBookHistoryKey, allBookHistories]);

  // FIX 2: Broken dep cycle fixed — bookHistory removed from deps, use ref instead
  useEffect(() => {
    if (!activeBookHistoryKey || !activeBookMeta) return;
    setAllBookHistories((prev) => {
      const currentEntry = prev[activeBookHistoryKey];
      const currentHistory = Array.isArray(currentEntry?.history)
        ? currentEntry.history
        : [];
      // Compare by length + first id to avoid unnecessary updates
      if (
        currentHistory.length === bookHistoryRef.current.length &&
        currentHistory[0]?.id === bookHistoryRef.current[0]?.id
      )
        return prev;

      const next = { ...prev };
      if (bookHistoryRef.current.length > 0) {
        next[activeBookHistoryKey] = {
          meta: activeBookMeta,
          history: bookHistoryRef.current,
        };
      } else {
        delete next[activeBookHistoryKey];
      }
      return next;
    });
  }, [activeBookHistoryKey, activeBookMeta]); // bookHistory intentionally omitted — using ref

  // Reset selection state when chapter or tab changes
  useEffect(() => {
    setSelectionAnchor(null);
    setSelectedHighlight("");
    setSelectionSummary("");
    setSelectionSummaryError("");
    setIsSummarizingSelection(false);
  }, [activeChapter, activeTab]);

  // FIX 3: Clean up selection timeout on unmount
  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current)
        clearTimeout(selectionTimeoutRef.current);
    };
  }, []);

  // ── MEMOIZED ACTIVE CHAPTER ───────────────────────────────────────────────
  // FIX 4: Replaces .filter().map() inside JSX — stable reference for React.memo
  const activeChapterData = useMemo(
    () =>
      ebookData?.chapters?.find((ch) => ch.chapter === activeChapter) ?? null,
    [ebookData?.chapters, activeChapter],
  );

  // ── HANDLERS (useCallback) ────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    try {
      setIsGenerating(true);
      setError(null);

      const res = await generateEbook({
        language,
        book_genre: bookType,
        book_title: initialTitle,
        chapter_count: initialChapterCount,
        tone: selectedTone,
        author_name: resolvedAuthorName,
        author_name_2: authorName2,
      });

      const data = res?.data?.data || res?.data || res;
      if (!data || typeof data !== "object")
        throw new Error("Invalid response");

      const displayTitle = cleanDisplayTitle(data.title, data.tagline);
      const displayTagline = cleanDisplayTagline(data.tagline);

      const safeChapters = Array.isArray(data.chapters)
        ? data.chapters.map((ch, i) => ({
            chapter: ch?.chapter || i + 1,
            title: sanitizeDisplayText(ch?.title, `Chapter ${i + 1}`),
            intro: sanitizeDisplayText(ch?.intro),
            quote: sanitizeDisplayText(ch?.quote),
            quoteAuthor: "AI",
            content: sanitizeDisplayText(ch?.content).slice(0, 2000),
            conclusion: sanitizeDisplayText(ch?.conclusion),
          }))
        : [];

      const safeCoverIdeas = Array.isArray(data.cover_ideas)
        ? data.cover_ideas.map((c, i) => ({
            id: i + 1,
            concept: `Concept ${i + 1}`,
            description: sanitizeDisplayText(c),
            palette: ["#1a1a2e", "#16213e", "#0f3460", "#e94560"],
          }))
        : [];

      const safeToc = Array.isArray(data.table_of_contents)
        ? data.table_of_contents.map((entry) => sanitizeDisplayText(entry))
        : [];

      const generatedBookMeta = buildBookMeta(
        displayTitle,
        resolvedAuthorName,
        bookType,
        language,
      );
      const generatedBookHistoryKey = buildBookHistoryKey(
        displayTitle,
        resolvedAuthorName,
        bookType,
        language,
      );
      const generatedHistoryEntry = {
        id: generateHistoryId(),
        event: "E-Book Generated",
        date: new Date().toLocaleString(),
        description: `${displayTitle} was generated with ${safeChapters.length} chapters in ${language}.`,
      };

      setActiveBookHistoryKey(generatedBookHistoryKey);
      setActiveBookMeta(generatedBookMeta);
      setBookHistory([generatedHistoryEntry]);
      setEbookData({
        title: displayTitle,
        tagline: displayTagline,
        author: [resolvedAuthorName, authorName2].filter(Boolean).join(", "),
        genre: bookType,
        language,
        publisher: "AI Publishing",
        publishDate: new Date().getFullYear().toString(),
        isbn: "AI-" + Date.now(),
        coverIdeas: safeCoverIdeas,
        table_of_contents: safeToc,
        chapters: safeChapters,
        outline: sanitizeDisplayText(data.outline),
        copyright: sanitizeDisplayText(data.copyright_page),
      });
      setSelectedCover(safeCoverIdeas[0] || null);
      setGenerationComplete(true);
      setActiveTab("cover");
    } catch (err) {
      console.error(err);
      setError("Failed to generate e-book. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [
    language,
    bookType,
    initialTitle,
    initialChapterCount,
    resolvedAuthorName,
    cleanDisplayTitle,
    cleanDisplayTagline,
    sanitizeDisplayText,
    buildBookMeta,
    buildBookHistoryKey,
    generateHistoryId,
    selectedTone,
    authorName2,
  ]);

  // FIX 5: buildFullText wrapped in useCallback
  const buildFullText = useCallback(() => {
    if (!ebookData) return "";
    const p = (s) => "\n\n" + s;
    let out = `${ebookData.title.toUpperCase()}\nBy ${ebookData.author}\n${ebookData.tagline}`;
    out += p(`COPYRIGHT\n${ebookData.copyright}`);
    out += p(`OUTLINE\n${ebookData.outline}`);
    out += p(`TABLE OF CONTENTS\n${ebookData.table_of_contents.join("\n")}`);
    ebookData.chapters.forEach((ch) => {
      out += p(`CHAPTER ${ch.chapter}: ${ch.title.toUpperCase()}`);
      out += p(ch.intro);
      out += p(`"${ch.quote}" — ${ch.quoteAuthor}`);
      out += p(ch.content);
      out += p(ch.conclusion);
    });
    return out;
  }, [ebookData]);

  // ✅ SAFE FILENAME (fix for ":" and invalid chars)
  const getSafeFileName = (name) => {
    return (name || "ebook")
      .replace(/[\\/:*?"<>|]/g, "") // remove invalid chars
      .replace(/\s+/g, "_") // spaces → underscore
      .replace(/_+/g, "_") // remove duplicates
      .trim();
  };

  // ✅ DOWNLOAD FUNCTION (stable)
  const triggerDownload = (blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 500);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  // ✅ FULL EXPORT FUNCTION
  const handleExport = (format) => {
    if (!ebookData) {
      alert("No data to export");
      return;
    }

    const filename = getSafeFileName(ebookData.title);
    const content = buildFullText();

    try {
      // ================= TXT =================
      if (format === "txt") {
        const blob = new Blob([content], {
          type: "text/plain;charset=utf-8",
        });

        triggerDownload(blob, `${filename}.txt`);
      }

      // ================= DOC =================
      else if (format === "doc") {
        const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${ebookData.title}</title>
        </head>
        <body>
          <h1>${ebookData.title}</h1>
          <h3>${ebookData.author}</h3>
          <hr/>
          <div style="white-space: pre-wrap; font-family: serif;">
${content}
          </div>
        </body>
        </html>
      `;

        const blob = new Blob(["\ufeff", html], {
          type: "application/msword;charset=utf-8",
        });

        triggerDownload(blob, `${filename}.doc`);
      }

      // ================= PDF =================
      else if (format === "pdf") {
        const iframe = document.createElement("iframe");

        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";

        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;

        doc.open();
        doc.write(`
        <html>
          <head>
            <title>${ebookData.title}</title> <!-- FIXED about:blank -->
            <style>
              body {
                font-family: serif;
                padding: 30px;
                line-height: 1.6;
              }
              h1, h3 {
                text-align: center;
              }
              pre {
                white-space: pre-wrap;
                font-family: serif;
              }
            </style>
          </head>
          <body>
            <h1>${ebookData.title}</h1>
            <h3>${ebookData.author}</h3>
            <hr/>
            <pre>${content}</pre>
          </body>
        </html>
      `);
        doc.close();

        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 400);

        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1500);
      }
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(buildFullText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [buildFullText]);

  const closeSelectionTools = useCallback(() => {
    window.getSelection?.()?.removeAllRanges?.();
    setSelectionAnchor(null);
    setSelectedHighlight("");
    setSelectionSummary("");
    setSelectionSummaryError("");
    setIsSummarizingSelection(false);
  }, []);

  // FIX 3 cont: debounced, stable — only touches refs and setters
  const handleTextSelect = useCallback(() => {
    if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
    selectionTimeoutRef.current = setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString()?.trim() || "";
      if (!text || text.length < 10 || selection?.rangeCount === 0) {
        setSelectionAnchor(null);
        setSelectedHighlight("");
        return;
      }
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      setSelectedHighlight(text);
      setSelectionAnchor({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }, 250);
  }, []);

  const summarizeSelectedText = useCallback(async () => {
    if (!selectedHighlight) return;
    setIsSummarizingSelection(true);
    setSelectionSummary("");
    setSelectionSummaryError("");
    try {
      const userId =
        sessionStorage.getItem("userId") ||
        localStorage.getItem("userId") ||
        "";
      const response = await getCheckSummariser({
        text: selectedHighlight,
        content: "",
        prompts: "one",
        userId,
        isCoAuthor: false,
      });
      const summaryText = extractSummaryText(response);
      if (!summaryText) throw new Error("Empty summary response");
      setSelectionSummary(summaryText);
      setBookHistory((prev) => [
        {
          id: generateHistoryId(),
          event: "Selected Text Summarized",
          date: new Date().toLocaleString(),
          description: `Summary created for ${selectedHighlight.split(/\s+/).length} selected words.`,
        },
        ...prev,
      ]);
    } catch (err) {
      console.error(err);
      setSelectionSummaryError("Failed to summarize the selected text.");
    } finally {
      setIsSummarizingSelection(false);
    }
  }, [selectedHighlight, extractSummaryText, generateHistoryId]);

  const handleDialogClose = useCallback(() => {
    setIsFullscreen(false);
    setEbookData(null);
    setBookHistory([]);
    setAllBookHistories({});
    setSelectedHighlight("");
    setSelectionSummary("");
    setSelectionAnchor(null);
    onClose();
  }, [onClose]);

  const renderedContent = useMemo(() => {
    if (!ebookData) return null;

    switch (activeTab) {
      case "cover":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#f59e0b",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 2,
              }}
            >
              COVER DESIGN IDEAS
            </Typography>

            {/* Main Cover Preview */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                mb: 4,
                background: selectedCover
                  ? `linear-gradient(160deg, ${selectedCover.palette[0]} 0%, ${selectedCover.palette[1]} 40%, ${selectedCover.palette[2]} 70%, ${selectedCover.palette[3] || selectedCover.palette[0]} 100%)`
                  : "linear-gradient(135deg, #1a1a2e, #16213e)",
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 4,
                py: 6,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.08), transparent 60%)",
                }}
              />
              {selectedCover && (
                <Chip
                  label={selectedCover.concept}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: alpha("#fff", 0.15),
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    backdropFilter: "blur(8px)",
                  }}
                />
              )}
              <AutoStories
                sx={{ fontSize: 52, color: alpha("#fff", 0.5), mb: 2 }}
              />
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{
                  color: "white",
                  fontFamily: "'Georgia', serif",
                  textShadow: "0 2px 20px rgba(0,0,0,0.5)",
                  mb: 1.5,
                  lineHeight: 1.2,
                }}
              >
                {ebookData.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha("#fff", 0.75),
                  fontFamily: "'Georgia', serif",
                  fontStyle: "italic",
                  maxWidth: 520,
                  lineHeight: 1.7,
                  mb: 2,
                }}
              >
                {ebookData.tagline}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha("#fff", 0.55),
                  fontWeight: 600,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                }}
              >
                {ebookData.author}
              </Typography>
              <Chip
                label={ebookData.genre}
                size="small"
                sx={{
                  mt: 2,
                  bgcolor: alpha("#fff", 0.12),
                  color: alpha("#fff", 0.8),
                  backdropFilter: "blur(8px)",
                  fontSize: "0.68rem",
                }}
              />
            </Box>

            {/* <Typography
              variant="overline"
              sx={{
                fontSize: "0.62rem",
                color: "text.secondary",
                fontWeight: 700,
                letterSpacing: 1.2,
                display: "block",
                mb: 1.5,
              }}
            >
              CHOOSE A COVER STYLE
            </Typography> */}
            <Grid container spacing={2}>
              {ebookData.coverIdeas.map((cover) => (
                <Grid xs={12} sm={4} key={cover.id}>
                  <CoverCard
                    cover={cover}
                    isSelected={selectedCover?.id === cover.id}
                    onSelect={setSelectedCover}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "copyright":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#6366f1",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 3,
              }}
            >
              COPYRIGHT & PUBLICATION DETAILS
            </Typography>
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha("#6366f1", 0.2)}`,
                    background: alpha("#6366f1", 0.04),
                  }}
                >
                  <Stack spacing={2.5}>
                    {[
                      { label: "Book Title", value: ebookData.title },
                      { label: "Author", value: ebookData.author },
                      { label: "Publisher", value: ebookData.publisher },
                      {
                        label: "Publication Year",
                        value: ebookData.publishDate,
                      },
                      { label: "Language", value: ebookData.language },
                      { label: "ISBN", value: ebookData.isbn },
                      { label: "Genre", value: ebookData.genre },
                    ].map((item) => (
                      <Box key={item.label}>
                        <Typography
                          variant="overline"
                          sx={{
                            fontSize: "0.58rem",
                            color: "#6366f1",
                            fontWeight: 700,
                            letterSpacing: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "text.primary", mt: 0.2 }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha("#6366f1", 0.2)}`,
                    background: alpha("#6366f1", 0.04),
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Copyright sx={{ color: "#6366f1" }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Legal Notice
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.9,
                      color: "text.primary",
                      fontFamily: "'Georgia', serif",
                    }}
                  >
                    {ebookData.copyright}
                  </Typography>
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha("#6366f1", 0.08),
                      border: `1px dashed ${alpha("#6366f1", 0.3)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6366f1",
                        fontWeight: 700,
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      ALL RIGHTS RESERVED
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unauthorized reproduction or distribution of this work
                      constitutes copyright infringement and may result in civil
                      and criminal penalties.
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case "title":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#ec4899",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 3,
              }}
            >
              TITLE & TAGLINE
            </Typography>
            <Box
              sx={{
                p: 5,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha("#ec4899", 0.08)}, ${alpha("#8b5cf6", 0.06)})`,
                border: `1px solid ${alpha("#ec4899", 0.2)}`,
                textAlign: "center",
                mb: 3,
              }}
            >
              <Chip
                label={ebookData.genre.toUpperCase()}
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: alpha("#ec4899", 0.12),
                  color: "#ec4899",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  letterSpacing: 1.5,
                }}
              />
              <Typography
                variant="h2"
                fontWeight={900}
                sx={{
                  fontFamily: "'Georgia', serif",
                  color: "text.primary",
                  lineHeight: 1.15,
                  mb: 2,
                  background: "linear-gradient(135deg, #ec4899, #8b5cf6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {ebookData.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Georgia', serif",
                  fontStyle: "italic",
                  color: "text.secondary",
                  maxWidth: 500,
                  mx: "auto",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                {ebookData.tagline}
              </Typography>
              <Divider sx={{ my: 3, borderColor: alpha("#ec4899", 0.2) }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                }}
              >
                By {ebookData.author}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <Chip
                  label={`${ebookData.chapters.length} Chapters`}
                  size="small"
                  sx={{
                    bgcolor: alpha("#ec4899", 0.1),
                    color: "#ec4899",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={ebookData.language}
                  size="small"
                  sx={{
                    bgcolor: alpha("#8b5cf6", 0.1),
                    color: "#8b5cf6",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={ebookData.publishDate}
                  size="small"
                  sx={{
                    bgcolor: alpha("#3b82f6", 0.1),
                    color: "#3b82f6",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Box>
          </Box>
        );

      case "outline":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#3b82f6",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 2,
              }}
            >
              BOOK OUTLINE
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2.5,
                border: `1px solid ${alpha("#3b82f6", 0.2)}`,
                background: alpha("#3b82f6", 0.03),
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 4,
                  bottom: 0,
                  background: "linear-gradient(180deg, #3b82f6, #8b5cf6)",
                  borderRadius: "4px 0 0 4px",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 2,
                  color: "text.primary",
                  fontFamily: "'Georgia', serif",
                  whiteSpace: "pre-line",
                  pl: 1,
                }}
              >
                {ebookData.outline}
              </Typography>
            </Paper>
          </Box>
        );

      case "toc":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#8b5cf6",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 2,
              }}
            >
              TABLE OF CONTENTS
            </Typography>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${alpha("#8b5cf6", 0.2)}`,
                overflow: "hidden",
              }}
            >
              {ebookData.table_of_contents.map((entry, i) => {
                const color = CHAPTER_COLORS[i % CHAPTER_COLORS.length];
                return (
                  <Box
                    key={i}
                    onClick={() => {
                      setActiveTab("chapters");
                      setActiveChapter(i + 1);
                    }}
                    sx={{
                      px: 3,
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      borderBottom:
                        i < ebookData.table_of_contents.length - 1
                          ? `1px solid ${alpha("#8b5cf6", 0.08)}`
                          : "none",
                      transition: "background 0.18s",
                      "&:hover": { background: alpha(color, 0.06) },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.6)})`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        flex: 1,
                        color: "text.primary",
                        fontSize: "0.9rem",
                      }}
                    >
                      {entry}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          height: 1,
                          width: 60,
                          borderBottom: "1px dashed",
                          borderColor: "text.disabled",
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight={600}
                      >
                        {i + 3}
                      </Typography>
                    </Box>
                    <ChevronRight sx={{ fontSize: 16, color }} />
                  </Box>
                );
              })}
            </Paper>
          </Box>
        );

      case "chapters":
        return (
          <Box>
            {/* Chapter Navigator Pills */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
            >
              {ebookData.chapters.map((ch, i) => {
                const color = CHAPTER_COLORS[i % CHAPTER_COLORS.length];
                const isActive = activeChapter === ch.chapter;
                return (
                  <Chip
                    key={ch.chapter}
                    label={`Ch.${ch.chapter}`}
                    size="small"
                    onClick={() => setActiveChapter(ch.chapter)}
                    sx={{
                      fontWeight: isActive ? 700 : 400,
                      bgcolor: isActive ? alpha(color, 0.15) : "transparent",
                      color: isActive ? color : "text.secondary",
                      border: `1px solid ${isActive ? alpha(color, 0.4) : alpha(theme.palette.divider, 0.3)}`,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": { bgcolor: alpha(color, 0.1), color },
                    }}
                  />
                );
              })}
            </Stack>

            {/* FIX 4: activeChapterData from useMemo — stable, no new array every render */}
            {activeChapterData && (
              <Box onMouseUp={handleTextSelect}>
                <ChapterFullView
                  chapter={activeChapterData}
                  chapterColor={
                    CHAPTER_COLORS[
                      (activeChapterData.chapter - 1) % CHAPTER_COLORS.length
                    ]
                  }
                  isActive
                />
              </Box>
            )}

            {/* Prev / Next */}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 2 }}
            >
              <Button
                startIcon={<NavigateBefore />}
                disabled={activeChapter <= 1}
                onClick={() => setActiveChapter((p) => Math.max(1, p - 1))}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Previous
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: "center" }}
              >
                {activeChapter} / {ebookData.chapters.length}
              </Typography>
              <Button
                endIcon={<NavigateNext />}
                disabled={activeChapter >= ebookData.chapters.length}
                onClick={() =>
                  setActiveChapter((p) =>
                    Math.min(ebookData.chapters.length, p + 1),
                  )
                }
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Next
              </Button>
            </Stack>

            {/* Selection Summary Panel */}
            {(selectedHighlight ||
              selectionSummary ||
              selectionSummaryError) && (
              <Fade in>
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha("#f59e0b", 0.3)}`,
                    bgcolor: alpha("#f59e0b", 0.06),
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: "0.6rem",
                        color: "#f59e0b",
                        fontWeight: 700,
                        letterSpacing: 1.2,
                      }}
                    >
                      CHAPTER TEXT SUMMARY
                    </Typography>
                    <IconButton size="small" onClick={closeSelectionTools}>
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>

                  {selectedHighlight && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'Georgia', serif",
                        fontStyle: "italic",
                        color: "text.primary",
                        lineHeight: 1.7,
                        borderLeft: "3px solid #f59e0b",
                        pl: 2,
                        mb: selectionSummary || selectionSummaryError ? 2 : 0,
                      }}
                    >
                      {selectedHighlight}
                    </Typography>
                  )}

                  {selectionSummary && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha("#10b981", 0.08),
                        border: `1px solid ${alpha("#10b981", 0.25)}`,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontSize: "0.6rem",
                          color: "#10b981",
                          fontWeight: 700,
                          letterSpacing: 1.2,
                          display: "block",
                          mb: 0.8,
                        }}
                      >
                        SUMMARY RESULT
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          lineHeight: 1.8,
                          fontFamily: "'Georgia', serif",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {selectionSummary}
                      </Typography>
                    </Box>
                  )}

                  {selectionSummaryError && (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                      {selectionSummaryError}
                    </Alert>
                  )}
                </Paper>
              </Fade>
            )}
          </Box>
        );

      case "history":
        return (
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.65rem",
                color: "#14b8a6",
                fontWeight: 700,
                letterSpacing: 1.5,
                display: "block",
                mb: 2,
              }}
            >
              BOOK HISTORY
            </Typography>
            <Stack spacing={1.5}>
              {Object.entries(allBookHistories).length === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha("#14b8a6", 0.15)}`,
                    bgcolor: alpha("#14b8a6", 0.03),
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No saved book history yet.
                  </Typography>
                </Paper>
              )}

              {Object.entries(allBookHistories).map(([bookKey, bookEntry]) => {
                const meta = bookEntry?.meta || {};
                const historyItems = Array.isArray(bookEntry?.history)
                  ? bookEntry.history
                  : [];
                const isCurrentBook = bookKey === activeBookHistoryKey;

                return (
                  <Accordion
                    key={bookKey}
                    defaultExpanded={isCurrentBook}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(isCurrentBook ? "#14b8a6" : theme.palette.divider, isCurrentBook ? 0.3 : 0.12)}`,
                      bgcolor: isCurrentBook
                        ? alpha("#14b8a6", 0.04)
                        : alpha("#0f172a", 0.01),
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack spacing={0.6} sx={{ width: "100%" }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            {meta.title || "Untitled Book"}
                          </Typography>
                          {isCurrentBook && (
                            <Chip
                              label="Current Book"
                              size="small"
                              sx={{
                                bgcolor: alpha("#14b8a6", 0.12),
                                color: "#14b8a6",
                                fontWeight: 700,
                              }}
                            />
                          )}
                          <Chip
                            label={`${historyItems.length} event${historyItems.length === 1 ? "" : "s"}`}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {meta.author || "Unknown Author"} •{" "}
                          {meta.genre || "Unknown Genre"} •{" "}
                          {meta.language || "Unknown Language"}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={0}>
                        {historyItems.map((item, i) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: "flex",
                              gap: 2,
                              pb: i < historyItems.length - 1 ? 3 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  bgcolor: alpha("#14b8a6", 0.15),
                                  border: "2px solid #14b8a6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <AutoAwesome
                                  sx={{ fontSize: 16, color: "#14b8a6" }}
                                />
                              </Box>
                              {i < historyItems.length - 1 && (
                                <Box
                                  sx={{
                                    width: 2,
                                    flex: 1,
                                    bgcolor: alpha("#14b8a6", 0.2),
                                    mt: 0.5,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ pt: 0.5, pb: 2 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 0.5 }}
                                flexWrap="wrap"
                              >
                                <Typography variant="body2" fontWeight={700}>
                                  {item.event}
                                </Typography>
                                <Chip
                                  label={item.date}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha("#14b8a6", 0.1),
                                    color: "#14b8a6",
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              </Stack>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.84rem" }}
                              >
                                {item.description}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Box>
        );

      default:
        return null;
    }
  }, [
    ebookData,
    activeTab,
    activeChapter,
    selectedCover,
    activeChapterData,
    handleTextSelect,
    closeSelectionTools,
    selectedHighlight,
    selectionSummary,
    selectionSummaryError,
    allBookHistories,
    activeBookHistoryKey,
    theme,
  ]);

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : handleDialogClose}
      fullScreen={isFullscreen}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: isFullscreen ? "100vw" : "96vw",
          maxWidth: isFullscreen ? "100vw" : 1300,
          height: isFullscreen ? "100vh" : "94vh",
          borderRadius: isFullscreen ? 0 : 3,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(160deg, #0d1117, #161b22)"
              : "linear-gradient(160deg, #fafbfc, #f1f5f9)",
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── TOP HEADER ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 1.8,
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          borderBottom: `1px solid ${alpha("#fff", 0.06)}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            background: "linear-gradient(135deg, #f59e0b, #ec4899)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(245,158,11,0.4)",
          }}
        >
          <AutoStories sx={{ color: "white", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ color: "white", fontSize: "0.95rem", lineHeight: 1.2 }}
            noWrap
          >
            {ebookData?.title || initialTitle || "E-Book Generator"}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha("#fff", 0.5) }}>
            {generationComplete
              ? `✓ Complete — ${ebookData?.chapters?.length} chapters`
              : isGenerating
                ? "Generating your e-book..."
                : "AI-Powered E-Book Creator"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <LightTooltip
            title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          >
            <IconButton
              size="small"
              onClick={() => setIsFullscreen((prev) => !prev)}
              sx={{
                color: alpha("#fff", 0.65),
                "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
              }}
            >
              {isFullscreen ? (
                <FullscreenExit fontSize="small" />
              ) : (
                <Fullscreen fontSize="small" />
              )}
            </IconButton>
          </LightTooltip>

          {generationComplete && (
            <>
              <LightTooltip title={copied ? "Copied!" : "Copy full book"}>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    color: copied ? "#10b981" : alpha("#fff", 0.65),
                    "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
                  }}
                >
                  {copied ? (
                    <CheckCircle fontSize="small" />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )}
                </IconButton>
              </LightTooltip>
              <LightTooltip title="Export">
                <IconButton
                  size="small"
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    color: alpha("#fff", 0.65),
                    "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
                  }}
                >
                  <Download fontSize="small" />
                </IconButton>
              </LightTooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    minWidth: 180,
                  },
                }}
              >
                <MenuItem onClick={() => handleExport("pdf")} sx={{ gap: 1.5 }}>
                  <PictureAsPdf sx={{ fontSize: 18, color: "#ef4444" }} />
                  <Typography variant="body2">Export as PDF</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport("doc")} sx={{ gap: 1.5 }}>
                  <Description sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography variant="body2">Export as DOC</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport("txt")} sx={{ gap: 1.5 }}>
                  <Subject sx={{ fontSize: 18, color: "#10b981" }} />
                  <Typography variant="body2">Export as TXT</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
          {!isGenerating && (
            <IconButton
              size="small"
              onClick={handleDialogClose}
              sx={{
                color: alpha("#fff", 0.55),
                "&:hover": { color: "white", bgcolor: alpha("#ef4444", 0.25) },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* ── BODY ───────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* LEFT SIDEBAR */}
        <Box
          sx={{
            width: 200,
            flexShrink: 0,
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            flexDirection: "column",
            background:
              theme.palette.mode === "dark"
                ? alpha("#0d1117", 0.9)
                : alpha("#fff", 0.75),
            overflowY: "auto",
          }}
        >
          {/* Generate / Regenerate button */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            {!isGenerating && !generationComplete ? (
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<PlayArrow />}
                onClick={handleGenerate}
                sx={{
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #f59e0b, #ec4899)",
                  boxShadow: "0 4px 14px rgba(245,158,11,0.3)",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "none",
                  "&:hover": { boxShadow: "0 6px 20px rgba(245,158,11,0.45)" },
                }}
              >
                Generate E-Book
              </Button>
            ) : isGenerating ? (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <CircularProgress size={24} sx={{ color: "#f59e0b" }} />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 1, fontSize: "0.72rem" }}
                >
                  Generating...
                </Typography>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={async () => {
                  setGenerationComplete(false);
                  setEbookData(null);
                  await handleGenerate();
                }}
                sx={{
                  borderRadius: 2,
                  fontSize: "0.75rem",
                  textTransform: "none",
                }}
              >
                Regenerate
              </Button>
            )}
          </Box>

          {/* Section nav */}
          <Box sx={{ flex: 1, p: 1, overflowY: "auto" }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.58rem",
                color: "text.disabled",
                fontWeight: 700,
                letterSpacing: 1.2,
                px: 1,
                display: "block",
                mb: 0.8,
                mt: 0.5,
              }}
            >
              SECTIONS
            </Typography>
            <Stack spacing={0.3}>
              {SECTION_TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const isEnabled = generationComplete;
                return (
                  <Box
                    key={tab.id}
                    onClick={() => isEnabled && setActiveTab(tab.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      px: 1.2,
                      py: 0.9,
                      borderRadius: 1.5,
                      cursor: isEnabled ? "pointer" : "default",
                      background: isActive
                        ? alpha(tab.color, 0.12)
                        : "transparent",
                      border: `1px solid ${isActive ? alpha(tab.color, 0.4) : "transparent"}`,
                      opacity: isEnabled ? 1 : 0.4,
                      transition: "all 0.2s",
                      "&:hover": isEnabled
                        ? {
                            background: alpha(tab.color, 0.08),
                            borderColor: alpha(tab.color, 0.25),
                          }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        color: isActive ? tab.color : "text.disabled",
                        display: "flex",
                        "& .MuiSvgIcon-root": { fontSize: 15 },
                      }}
                    >
                      {tab.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 700 : 400}
                      sx={{
                        color: isActive
                          ? tab.color
                          : isEnabled
                            ? "text.primary"
                            : "text.disabled",
                        fontSize: "0.78rem",
                      }}
                    >
                      {tab.label}
                    </Typography>
                    {isActive && (
                      <FiberManualRecord
                        sx={{ fontSize: 7, color: tab.color, ml: "auto" }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>

            {/* Chapter quick links */}
            {generationComplete && ebookData && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: "0.58rem",
                    color: "text.disabled",
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    px: 1,
                    display: "block",
                    mb: 0.8,
                    mt: 2,
                  }}
                >
                  CHAPTERS
                </Typography>
                <Stack spacing={0.2}>
                  {ebookData.chapters.map((ch, i) => {
                    const color = CHAPTER_COLORS[i % CHAPTER_COLORS.length];
                    const isChActive =
                      activeTab === "chapters" && activeChapter === ch.chapter;
                    return (
                      <Box
                        key={ch.chapter}
                        onClick={() => {
                          setActiveTab("chapters");
                          setActiveChapter(ch.chapter);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.6,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          background: isChActive
                            ? alpha(color, 0.1)
                            : "transparent",
                          transition: "background 0.15s",
                          "&:hover": { background: alpha(color, 0.07) },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: alpha(color, isChActive ? 0.25 : 0.12),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.6rem", fontWeight: 700, color }}
                          >
                            {ch.chapter}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isChActive ? color : "text.secondary",
                            fontWeight: isChActive ? 700 : 400,
                            fontSize: "0.72rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ch.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}
          </Box>
        </Box>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.divider, 0.3),
              borderRadius: 3,
            },
          }}
        >
          {/* Not generated yet */}
          {!generationComplete && !isGenerating && (
            <Box sx={{ textAlign: "center", py: 12, px: 4 }}>
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: 4,
                  background: "linear-gradient(135deg, #f59e0b, #ec4899)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  boxShadow: "0 12px 40px rgba(245,158,11,0.3)",
                }}
              >
                <AutoStories sx={{ color: "white", fontSize: 44 }} />
              </Box>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Ready to Create Your E-Book
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 460, mx: "auto", mb: 4, lineHeight: 1.75 }}
              >
                Click <strong>Generate E-Book</strong> to create a complete book
                with cover ideas, copyright page, title, outline, table of
                contents, all chapters, summary, and history.
              </Typography>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ maxWidth: 500, mx: "auto" }}
              >
                {SECTION_TABS.map((tab) => (
                  <Grid xs={6} sm={3} key={tab.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(tab.color, 0.25)}`,
                        bgcolor: alpha(tab.color, 0.05),
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          color: tab.color,
                          mb: 0.5,
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                        }}
                      >
                        {tab.icon}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.68rem",
                          color: tab.color,
                          fontWeight: 600,
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Generating skeleton */}
          {isGenerating && (
            <Box sx={{ px: 4, py: 6 }}>
              <Stack spacing={2}>
                {SECTION_TABS.map((tab, i) => (
                  <Box
                    key={tab.id}
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Skeleton
                      variant="circular"
                      width={32}
                      height={32}
                      animation={i < 3 ? "wave" : false}
                    />
                    <Skeleton
                      variant="rounded"
                      height={48}
                      sx={{ flex: 1, borderRadius: 2 }}
                      animation={i < 3 ? "wave" : false}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Generated content */}
          {generationComplete && ebookData && (
            <Fade in>
              <Box sx={{ p: { xs: 2, md: 4 } }}>
                {/* Breadcrumb */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <AutoStories sx={{ fontSize: 15, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {ebookData.title}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    ›
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color:
                        SECTION_TABS.find((t) => t.id === activeTab)?.color ||
                        "#4A90E2",
                    }}
                  >
                    {SECTION_TABS.find((t) => t.id === activeTab)?.label}
                    {activeTab === "chapters"
                      ? ` — Chapter ${activeChapter}`
                      : ""}
                  </Typography>
                </Stack>

                {/* FIX 6: renderedContent from useMemo, not renderContent() call */}
                {renderedContent}

                {/* Selection Popover */}
                <Popover
                  open={Boolean(selectionAnchor && selectedHighlight)}
                  onClose={() => setSelectionAnchor(null)}
                  anchorReference="anchorPosition"
                  anchorPosition={
                    selectionAnchor
                      ? {
                          top: Math.round(selectionAnchor.top),
                          left: Math.round(selectionAnchor.left),
                        }
                      : undefined
                  }
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2.5,
                      boxShadow: "0 14px 40px rgba(15,23,42,0.16)",
                      border: `1px solid ${alpha("#f59e0b", 0.2)}`,
                      overflow: "hidden",
                    },
                  }}
                >
                  <Box sx={{ p: 1.25, maxWidth: 360 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        mb: 1,
                        lineHeight: 1.5,
                      }}
                    >
                      Summarize the selected chapter text
                    </Typography>
                    {selectionSummary && (
                      <Box
                        sx={{
                          mb: 1.25,
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: alpha("#10b981", 0.08),
                          border: `1px solid ${alpha("#10b981", 0.22)}`,
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            display: "block",
                            fontSize: "0.58rem",
                            color: "#10b981",
                            fontWeight: 700,
                            letterSpacing: 1.1,
                            mb: 0.5,
                          }}
                        >
                          Summary Result
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.primary",
                            lineHeight: 1.6,
                            fontSize: "0.82rem",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {selectionSummary}
                        </Typography>
                      </Box>
                    )}
                    {selectionSummaryError && (
                      <Alert
                        severity="error"
                        sx={{ mb: 1.25, borderRadius: 2 }}
                      >
                        {selectionSummaryError}
                      </Alert>
                    )}
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          isSummarizingSelection ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <Summarize />
                          )
                        }
                        onClick={summarizeSelectedText}
                        disabled={isSummarizingSelection}
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          background:
                            "linear-gradient(135deg, #f59e0b, #ec4899)",
                        }}
                      >
                        {isSummarizingSelection
                          ? "Summarizing..."
                          : "Summarize"}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={closeSelectionTools}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        {selectionSummary ? "Close" : "Cancel"}
                      </Button>
                    </Stack>
                  </Box>
                </Popover>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default EBookScenario;
